import { Collapse, Grid, LinearProgress, Typography } from '@mui/material';
import { useState } from 'react';
import { useAppSelector } from 'src/hooks';
import useCommentDrawer from './comment-drawer.hook';
import { IBulkCommentDrawerItem } from '../../types/bulk-comment-drawer';
import CommentInputControl from '../comments/comment-input-control';
import ProductCommentsContainer from '../comments/comment-list';
import ItemHeader from './item-header';
import { ICommentActionProps } from '../../types/ppg-sku-drawer.model';

/* eslint-disable react/require-default-props */
interface IProductWithCommentProps extends ICommentActionProps {
  product: IBulkCommentDrawerItem;
  showImage: boolean;
  isChild?: boolean;
  onCommentAdd: (comment: string) => Promise<unknown>;
}
/* eslint-enable react/require-default-props */

function ProductWithComment({
  product,
  showImage,
  isChild = false,
  onCommentAdd,
  onCommentDelete,
  onCommentUpdate
}: IProductWithCommentProps) {
  const [isExpanded, setIsExpanded] = useState(false);

  return (
    <>
      <Grid container alignItems='center' item xs={12}>
        <Grid p={1.5} item xs={12}>
          <ItemHeader
            onExpandToggleClick={() => {
              setIsExpanded(!isExpanded);
            }}
            isExpandable
            isOpen={isExpanded}
            showImage={showImage}
            name={product.itemName}
            imageSize='small'
            imgSrc={product.imageUrl}
            textProps={{
              fontWeight: isChild ? 400 : 700
            }}
          />
        </Grid>
      </Grid>
      <Grid item xs={12}>
        <Collapse in={isExpanded} timeout='auto'>
          <Grid p={1.5} item xs={12}>
            <CommentInputControl
              initialComment=''
              onCommentAdd={onCommentAdd}
            />
            <ProductCommentsContainer
              comments={product.comments}
              onCommentDelete={onCommentDelete}
              onCommentUpdate={onCommentUpdate}
            />
          </Grid>
        </Collapse>
      </Grid>
    </>
  );
}

export default function BulkCommentsDrawer() {
  const productsWithComments = useAppSelector(
    (state) => state.planner.productsWithComments
  );
  const { isLoading } = useAppSelector(
    (state) => state.planner.drawerViewMetadata
  );
  const {
    handleAddCommentForChildItem,
    handleAddCommentForParentItem,
    handleDeleteCommentForChildItem,
    handleDeleteCommentForParentItem,
    handleUpdateCommentForChildItem,
    handleUpdateCommentForParentItem
  } = useCommentDrawer();
  if (isLoading) {
    return <LinearProgress />;
  }
  return (
    <Grid container>
      {productsWithComments.map((product) => (
        <Grid
          container
          item
          xs={12}
          mb={1.5}
          sx={{
            backgroundColor: '#F2F2F2',
            borderRadius: 0.5
          }}
        >
          <ProductWithComment
            key={product.parentId}
            product={product}
            showImage
            onCommentAdd={(comment: string) =>
              handleAddCommentForParentItem(
                comment,
                product.id,
                product.parentId,
                product.index
              )
            }
            onCommentDelete={(commentId: number) =>
              handleDeleteCommentForParentItem(commentId, product.index)
            }
            onCommentUpdate={(commentId, updatedComment) =>
              handleUpdateCommentForParentItem(
                commentId,
                updatedComment,
                product.index
              )
            }
          />
          {product.children.length > 0 ? (
            <>
              <Grid px={1.5} item xs={12}>
                <Typography fontSize='12px' color='black' fontWeight={700}>
                  List of SKUs : {product.children.length}
                </Typography>
              </Grid>
              {product.children.map((child) => (
                <Grid
                  item
                  key={child.id}
                  xs={12}
                  sx={{ m: 1, border: '1px solid #E6E6E6', borderRadius: 0.5 }}
                >
                  <ProductWithComment
                    product={child}
                    showImage={false}
                    isChild
                    onCommentAdd={(comment: string) =>
                      handleAddCommentForChildItem(
                        comment,
                        child.parentId,
                        child.id,
                        product.index,
                        child.index
                      )
                    }
                    onCommentDelete={(commentId: number) =>
                      handleDeleteCommentForChildItem(
                        commentId,
                        product.index,
                        child.index
                      )
                    }
                    onCommentUpdate={(commentId, updatedComment) =>
                      handleUpdateCommentForChildItem(
                        commentId,
                        updatedComment,
                        product.index,
                        child.index
                      )
                    }
                  />
                </Grid>
              ))}
            </>
          ) : null}
        </Grid>
      ))}
    </Grid>
  );
}
